import { ITab } from './../../components/TabsWithContent'
import {
  ICompanyMember,
  ICompanyMemberPure,
  IRelationshipBasedSettings,
  ICompanyPure,
  ICompanyTerm,
  ICompanyJobLevelPure,
  ICompanyJobLevel,
  ICompanyJobPure,
  ICompany,
  ICompanyCultureLevel,
  ICompanyCultureLevelPure,
  ICompanyCultureRankPure,
  ICompanySkillPure,
} from './../models/Company'
import {
  isMemberBuddyOfAnotherMember,
  isMemberGenerallyLeadOfMember,
  isSupport,
} from './isLeadOf'
import { PdfExportScope, PdfBulkEntry } from './../models/PdfExport'

// Resolve a member by id across the FULL company roster — regular members AND
// client-lead/support members. The latter live in `clientLeads`, not
// `allMembers` (which filters out support), so feedback to/from a client lead
// otherwise resolves to undefined (blank name + crash on drill-in).
export const findCompanyMemberById = (
  company: Pick<ICompany, 'allMembers' | 'clientLeads'>,
  id?: string
): ICompanyMember | undefined =>
  id
    ? company.allMembers.find((m) => m._id === id) ||
      company.clientLeads?.find((m) => m._id === id)
    : undefined

export const getCurrentTerm = (
  company: ICompanyPure
): ICompanyTerm | undefined => {
  return company.termsInformation.terms.find(
    (term) => term.termNumber === company.state.currentTermNumber
  )
}

// Join a value's levels to the company's culture ranks, so `getCultureTabs` can
// label each level. Levels whose rank no longer exists are dropped rather than
// throwing: this runs on the public Employer Branding site, where a deleted rank
// must degrade to one tab fewer, not to a blank page.
// Generic over the value shape: the public Culture page passes a whitelisted
// projection of a cultural value, not the full company sub-document.
export const hydrateCultureLevels = <
  T extends { levels: ICompanyCultureLevelPure[] }
>(
  cultureValue: T,
  ranks: ICompanyCultureRankPure[]
): T & { levels: ICompanyCultureLevel[] } => ({
  ...cultureValue,
  levels: cultureValue.levels.flatMap((level) => {
    const rank = ranks.find(
      (r) => String(r._id) === String(level.cultureRankId)
    )
    return rank ? { ...level, rank: rank.rank, title: rank.title } : []
  }),
})

// Only reads `levels`, so it accepts any hydrated value shape.
export const getCultureTabs = (
  cultureValue: { levels: ICompanyCultureLevel[] },
  memberLevel?: ICompanyJobLevelPure
): ITab[] | undefined => {
  return cultureValue.levels?.length === 1
    ? (cultureValue.levels[0].description || '').length === 0
      ? undefined
      : [
          {
            richText: cultureValue.levels[0].description || '',
            openByDefault: true,
          },
        ]
    : cultureValue.levels
        .map((level) => {
          return {
            tag: level.title,
            richText: level.description || '',
            openByDefault: level.cultureRankId === memberLevel?.cultureRankId,
            rank: level.rank,
          }
        })
        .sort((a, b) => a.rank - b.rank)
}

// Whose scores count towards a final score when a company has not configured it
export const DEFAULT_SCORE_CONTRIBUTORS: IRelationshipBasedSettings = {
  leads: true,
  buddies: true,
  peers: true,
  self: false,
}

export const getAllowedContributors = (
  company: ICompanyPure | ICompany,
  scoredMember: ICompanyMemberPure,
  settings?: IRelationshipBasedSettings | undefined
): ICompanyMemberPure[] => {
  const members = 'allMembers' in company ? company.allMembers : company.members
  const config = settings || company.settings?.evaluations?.scoreContributors
  // Exclude support from all scores, regardless of the contributor config
  if (!config) {
    return members.filter((m) => !isSupport(m))
  }
  return members.flatMap((m) => {
    if (isSupport(m)) return []
    const isSelf = String(m._id) === String(scoredMember._id)
    if (config.self && isSelf) {
      return m
    }
    const isBuddy = isMemberBuddyOfAnotherMember(m, scoredMember)
    if (config.buddies && isBuddy && !isSelf) {
      return m
    }
    const isLead = isMemberGenerallyLeadOfMember(m, scoredMember)
    if (config.leads && isLead) {
      return m
    }

    if (config.peers && !isSelf && !isBuddy && !isLead) {
      return m
    }
    return []
  })
}

/**
 * @returns mutable reference to job object inside the company
 */
export const getJobLevelById = (
  levelId: string,
  company: Pick<ICompanyPure, 'jobs'>
): {
  level: ICompanyJobLevelPure | undefined
  job: ICompanyJobPure | undefined
} => {
  if (!company.jobs) {
    return { level: undefined, job: undefined }
  }
  const job = company.jobs.find((j) =>
    j.levels.some((l) => l._id + '' === levelId + '')
  )
  const level =
    levelId !== undefined && job
      ? job.levels.find((jobLevel) => levelId + '' === jobLevel._id + '')
      : undefined

  return { level, job }
}

export const getJobsAttachedToSkill = (
  skillId: string,
  company: ICompanyPure
): ICompanyJobLevel[] | undefined => {
  return company.jobs.flatMap((j) =>
    j.levels.filter((l) => l.skillsIds.includes(skillId))
  )
}

/**
 * A job level's skills, in the order the level stores them. `skillsIds` is the
 * only place that order lives, so walk it — filtering the library instead
 * (`company.skills.filter((s) => level.skillsIds.includes(s._id))`) reads as the
 * same thing but prints in record-creation order, which is a different list.
 *
 * Resolving those ids carries three decisions that have to be the same on every
 * surface, which is the real reason this is a function. An id that no longer
 * resolves is dropped — production data has drifted, and a throw here takes a
 * whole company load down with it. A soft-deleted record is left out unless
 * asked for: `includeInactive` is for historical data (evaluations, feedback)
 * that still points at records an admin has since removed. And ids compare as
 * strings, because the schema stores ObjectIds while the frontend types them as
 * `string`, and both shapes reach this function.
 */
export const getLevelSkills = (
  level: Pick<ICompanyJobLevelPure, 'skillsIds'> | undefined,
  company: Pick<ICompanyPure, 'skills'>,
  options?: { includeInactive?: boolean }
): ICompanySkillPure[] => {
  if (!level?.skillsIds?.length) return []

  const byId = new Map(
    (company.skills || []).map((skill) => [String(skill._id), skill])
  )

  return level.skillsIds
    .map((id) => byId.get(String(id)))
    .filter(
      (skill): skill is ICompanySkillPure =>
        !!skill && (!!options?.includeInactive || !skill.inactive)
    )
}

/** A single job by id — the mutable reference inside the company. */
export const getJobById = (
  jobId: string,
  company: ICompanyPure
): ICompanyJobPure | undefined =>
  company.jobs?.find((j) => j._id + '' === jobId + '')

/** Jobs belonging to a department. */
export const getJobsInDepartment = (
  departmentId: string,
  company: ICompanyPure
): ICompanyJobPure[] => {
  const department = company.departments?.find(
    (d) => d._id + '' === departmentId + ''
  )
  if (!department) return []
  return company.jobs.filter((j) =>
    department.jobsIds.some((id) => id + '' === j._id + '')
  )
}

/** All levels of every job in a department. */
export const getJobLevelsInDepartment = (
  departmentId: string,
  company: ICompanyPure
): ICompanyJobLevelPure[] =>
  getJobsInDepartment(departmentId, company).flatMap((j) => j.levels)

/** Every job level across the whole company. */
export const getAllJobLevels = (
  company: ICompanyPure
): ICompanyJobLevelPure[] => company.jobs?.flatMap((j) => j.levels) || []

/**
 * The jobs a brand new growth path starts with: one per cultural phase, so the
 * ladder mirrors the phases the company already uses. A company that doesn't use
 * phases carries a single rank, which would leave the growth path with one job -
 * and a one-job growth path is rendered as a standalone job everywhere, so by default 3.
 */
export const buildGrowthPathLevels = (
  cultureRanks: ICompanyCultureRankPure[],
  jobLabel: string
): ICompanyJobLevelPure[] => {
  const rankIds =
    cultureRanks.length > 1
      ? cultureRanks.map((rank) => rank._id)
      : new Array(3).fill(undefined)
  return rankIds.map((cultureRankId, index) => ({
    cultureRankId,
    title: `${jobLabel} ${index + 1}`,
    rank: index + 1,
    skillsIds: [],
  }))
}

/**
 * The rung directly above this one on its own growth path — the next step a
 * job level offers. Read from the job rather than from any view's own data: a
 * filter can hide that rung without changing the fact that the ladder goes on.
 */
export const getNextJobLevel = (
  job: ICompanyJobPure | undefined,
  level: ICompanyJobLevelPure | undefined
): ICompanyJobLevelPure | undefined =>
  job && level
    ? job.levels
        .filter((other) => other.rank > level.rank)
        .sort((a, b) => a.rank - b.rank)[0]
    : undefined

/**
 * The job levels a level reports to. This is the single read path for the
 * reporting relation — the job profile, the exports and the org diagram all go
 * through it. Ids that no longer resolve are dropped silently: a level can be
 * deleted by a path that predates the cleanup, and production data may already
 * have drifted.
 */
export const getReportsToLevels = (
  level: ICompanyJobLevelPure | undefined,
  company: Pick<ICompanyPure, 'jobs'>
): ICompanyJobLevelPure[] =>
  (level?.reportsToJobLevelIds || [])
    .map((id) => getJobLevelById(id + '', company).level)
    .filter((l): l is ICompanyJobLevelPure => !!l)

/**
 * Whether pointing `levelId` at `candidateIds` would close a reporting loop —
 * i.e. whether `levelId` is reachable by walking up from any candidate. Also
 * catches a self-reference, which is just the shortest loop. Shared by every
 * write path so the rule lives in exactly one place.
 *
 * The visited set bounds the walk, so this terminates even on data that is
 * already cyclic. That guard is deliberate, not defensive clutter: the API
 * rejects cycles, but onboardCompany.script writes job levels straight to the
 * database and bypasses these validators.
 */
export const wouldCreateReportingCycle = (
  levelId: string,
  candidateIds: string[],
  company: ICompanyPure
): boolean => {
  const target = levelId + ''
  const seen = new Set<string>()
  const queue = candidateIds.map((id) => id + '')
  while (queue.length) {
    const current = queue.shift() as string
    if (current === target) return true
    if (seen.has(current)) continue
    seen.add(current)
    const { level } = getJobLevelById(current, company)
    ;(level?.reportsToJobLevelIds || []).forEach((id) => queue.push(id + ''))
  }
  return false
}

/**
 * The job-level ids a bulk scope covers, in document order — the single source of
 * truth for "what gets rendered". LEVEL resolves to just itself; JOB/DEPARTMENT/
 * COMPANY expand to all levels they contain.
 */
export const getJobLevelIdsByScope = (
  scope: PdfExportScope,
  scopeId: string | undefined,
  company: ICompanyPure
): string[] => {
  if (scope === PdfExportScope.LEVEL) return scopeId ? [scopeId] : []
  const levels =
    scope === PdfExportScope.JOB
      ? getJobById(scopeId || '', company)?.levels || []
      : scope === PdfExportScope.DEPARTMENT
      ? getJobLevelsInDepartment(scopeId || '', company)
      : scope === PdfExportScope.COMPANY
      ? getAllJobLevels(company)
      : []
  return levels.map((l) => l._id + '')
}

/**
 * Human-facing breakdown of what a bulk export scope covers. `titles` is the list
 * the caller formats per scope: LEVEL → [job, level]; JOB → its level titles;
 * DEPARTMENT / COMPANY → the job titles included.
 */
export interface IBulkScopeSummary {
  count: number
  titles: string[]
}

// Summarize a bulk scope for the export modal's selection preview. Pure: returns
// data, the component handles i18n. Reuses the level resolvers above.
export const summarizeBulkScope = (
  scope: PdfExportScope,
  scopeId: string | undefined,
  company: ICompanyPure
): IBulkScopeSummary => {
  const count = getJobLevelIdsByScope(scope, scopeId, company).length

  if (scope === PdfExportScope.LEVEL) {
    const { job, level } = getJobLevelById(scopeId || '', company)
    return {
      count,
      titles: [job?.title, level?.title].filter((t): t is string => !!t),
    }
  }

  if (scope === PdfExportScope.JOB) {
    const levels = getJobById(scopeId || '', company)?.levels || []
    return { count, titles: levels.map((l) => l.title) }
  }

  // DEPARTMENT / COMPANY: list the jobs included.
  const includedJobs =
    scope === PdfExportScope.DEPARTMENT
      ? getJobsInDepartment(scopeId || '', company)
      : company.jobs || []
  return { count, titles: includedJobs.map((j) => j.title) }
}

// Ordered cover + profile entries for a bulk export, in document order. Covers
// mark grouping boundaries only (company / department / job) — individual profiles
// never get one, and a single-level (LEVEL) export gets no cover at all. Empty jobs
// and departments are skipped. A job is emitted under its FIRST owning department
// only (mirrors buildJobProfileData's `.find`); jobs with no department trail at the
// company root.
export const buildBulkSections = (
  scope: PdfExportScope,
  scopeId: string | undefined,
  company: ICompanyPure
): PdfBulkEntry[] => {
  const companyName = company.name

  // A job: its cover (listing its levels), then one profile entry per level.
  // `departmentId` is the owning domain, so the cover can inherit its brand color.
  const jobEntries = (
    job: ICompanyJobPure,
    parents: string[],
    departmentId?: string
  ): PdfBulkEntry[] =>
    job.levels.length
      ? [
          {
            type: 'cover',
            level: 'job',
            breadcrumb: [...parents, job.title],
            contents: job.levels.map((l) => l.title),
            departmentId,
          },
          ...job.levels.map(
            (l): PdfBulkEntry => ({ type: 'profile', jobLevelId: l._id + '' })
          ),
        ]
      : []

  if (scope === PdfExportScope.LEVEL) {
    return scopeId ? [{ type: 'profile', jobLevelId: scopeId }] : []
  }

  if (scope === PdfExportScope.JOB) {
    const job = getJobById(scopeId || '', company)
    if (!job) return []
    const dept = company.departments?.find((d) =>
      d.jobsIds.some((id) => id + '' === job._id + '')
    )
    return jobEntries(
      job,
      dept ? [companyName, dept.name] : [companyName],
      dept?._id ? dept._id + '' : undefined
    )
  }

  if (scope === PdfExportScope.DEPARTMENT) {
    const dept = company.departments?.find((d) => d._id + '' === scopeId + '')
    if (!dept) return []
    const jobs = getJobsInDepartment(scopeId || '', company).filter(
      (j) => j.levels.length
    )
    if (!jobs.length) return []
    return [
      {
        type: 'cover',
        level: 'department',
        breadcrumb: [companyName, dept.name],
        contents: jobs.map((j) => j.title),
        departmentId: dept._id + '',
      },
      ...jobs.flatMap((j) =>
        jobEntries(j, [companyName, dept.name], dept._id + '')
      ),
    ]
  }

  // COMPANY: company cover (listing its departments, or jobs when there are none)
  // → per department (cover + its jobs) → any jobs with no department, as covers at
  // the company root.
  const companyContents: string[] = []
  const sections: PdfBulkEntry[] = []
  const claimed = new Set<string>()
  for (const dept of company.departments || []) {
    const jobs = getJobsInDepartment(dept._id + '', company).filter(
      (j) => j.levels.length && !claimed.has(j._id + '')
    )
    if (!jobs.length) continue
    jobs.forEach((j) => claimed.add(j._id + ''))
    companyContents.push(dept.name)
    sections.push({
      type: 'cover',
      level: 'department',
      breadcrumb: [companyName, dept.name],
      contents: jobs.map((j) => j.title),
      departmentId: dept._id + '',
    })
    jobs.forEach((j) =>
      sections.push(...jobEntries(j, [companyName, dept.name], dept._id + ''))
    )
  }
  for (const job of company.jobs || []) {
    if (claimed.has(job._id + '') || !job.levels.length) continue
    companyContents.push(job.title)
    sections.push(...jobEntries(job, [companyName]))
  }
  return [
    {
      type: 'cover',
      level: 'company',
      breadcrumb: [companyName],
      contents: companyContents,
    },
    ...sections,
  ]
}

// Turns a free-text company name into a URL-safe slug (lowercase, dashes, no
// leading/trailing dashes). Shared by the create UI (slug preview) and the backend
// create module so both derive the same slug from a name.
export const slugify = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
